import React, { useState, useRef } from "react";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { Send, Loader2 } from "lucide-react";
import { useT } from "@/i18n/LanguageProvider";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

function GeminiSpark({ size = 22 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <defs>
        <linearGradient id="gemini-grad" x1="0" y1="0" x2="24" y2="24" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#4285F4" />
          <stop offset="0.5" stopColor="#9b72cb" />
          <stop offset="1" stopColor="#EA4335" />
        </linearGradient>
      </defs>
      <path
        d="M12 0c.4 5.2 6.4 11.6 12 12-5.6.4-11.6 6.8-12 12-.4-5.2-6.4-11.6-12-12C5.6 11.6 11.6 5.2 12 0z"
        fill="url(#gemini-grad)"
      />
    </svg>
  );
}

export default function GeminiButton() {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [response, setResponse] = useState("");
  const [loading, setLoading] = useState(false);
  const abortRef = useRef(null);

  const submit = async () => {
    const q = prompt.trim();
    if (!q || loading) return;
    setLoading(true);
    setResponse("");

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const res = await fetch(`${BACKEND_URL}/api/gemini/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ prompt: q }),
        signal: controller.signal,
      });
      if (!res.ok) {
        const errText = await res.text();
        setResponse(`Error ${res.status}: ${errText.slice(0, 200)}`);
        return;
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        acc += decoder.decode(value, { stream: true });
        setResponse(acc);
      }
    } catch (e) {
      if (e.name !== "AbortError") {
        setResponse(`Network error: ${e.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    if (abortRef.current) abortRef.current.abort();
    setPrompt("");
    setResponse("");
    setLoading(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) reset();
      }}
    >
      <DialogTrigger asChild>
        <button
          type="button"
          data-testid="gemini-trigger"
          aria-label="Ask Gemini"
          className="fixed bottom-20 right-4 md:bottom-24 md:right-5 z-40 h-12 w-12 rounded-full bg-white border border-stone-200 shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-150 flex items-center justify-center"
          title="Ask Gemini"
        >
          <GeminiSpark size={24} />
        </button>
      </DialogTrigger>

      <DialogContent
        data-testid="gemini-dialog"
        className="max-w-lg border border-stone-700 bg-stone-950 text-stone-100 rounded-sm p-0 overflow-hidden"
      >
        <div className="px-5 py-4 border-b border-stone-800 flex items-center gap-3">
          <GeminiSpark size={20} />
          <div>
            <h2 className="font-display text-base tracking-tight">{t("gemini.title")}</h2>
            <p className="text-[10px] tracking-[0.14em] uppercase text-stone-500">
              gemini-3.5-flash · clinical assistant
            </p>
          </div>
        </div>

        <div className="px-5 py-4 space-y-3">
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) submit();
            }}
            placeholder={t("gemini.placeholder")}
            rows={3}
            data-testid="gemini-input"
            className="w-full bg-stone-900 border border-stone-800 focus:border-sky-700 focus:outline-none focus:ring-1 focus:ring-sky-700/30 rounded-sm px-3 py-2.5 text-sm text-stone-100 placeholder:text-stone-600 font-sans min-h-[80px] resize-none"
          />
          <div className="flex items-center justify-between gap-2">
            <span className="text-[10px] text-stone-500 font-mono">
              ⌘ / Ctrl + Enter
            </span>
            <button
              type="button"
              onClick={submit}
              disabled={loading || !prompt.trim()}
              data-testid="gemini-submit"
              className="inline-flex items-center gap-1.5 bg-sky-700 text-white hover:bg-sky-600 disabled:bg-stone-800 disabled:text-stone-600 disabled:cursor-not-allowed transition-colors duration-100 rounded-sm px-3 py-2 min-h-[40px] text-sm font-medium"
            >
              {loading ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              {loading ? t("gemini.thinking") : t("gemini.ask")}
            </button>
          </div>
        </div>

        {response && (
          <div className="border-t border-stone-800 px-5 py-4 bg-stone-900/50 max-h-[40vh] overflow-y-auto">
            <div className="text-[10px] tracking-[0.14em] uppercase font-semibold text-sky-400 mb-2">
              Response
            </div>
            <pre
              data-testid="gemini-response"
              className="whitespace-pre-wrap text-sm text-stone-200 leading-relaxed font-sans"
            >
              {response}
            </pre>
          </div>
        )}

        <div className="px-5 py-2 border-t border-stone-800 text-[10px] leading-snug text-stone-500">
          {t("gemini.disclaimer")}
        </div>
      </DialogContent>
    </Dialog>
  );
}
